import { startWorkerClient, type WorkerClientHandle, type StatsMsg } from "../sdk";
import { MESH_URL } from "./mesh-config";
import { drawCellGrid, categoryColor } from "./materialise-grid";

// The Contribute page — a focused, full-page experience for the worker role: "donate spare
// browser compute to live jobs." Rendered as an overlay (like About) at #contribute, and the
// "Add worker" button deep-links a fresh tab here via ?worker=1.
//
// Consent-forward by decision: nothing computes until the visitor clicks Start. A visible
// banner, a one-click Stop, and a CPU duty-cycle control satisfy the coordinator's
// non-negotiable consent rule (coordinator/CLAUDE.md). Live counters + a recent-chunk feed
// make "I'm helping" tangible.

let el: HTMLElement | null = null;
let handle: WorkerClientHandle | null = null;
let chunksDone = 0;
let duty = 0.75;

// The contribution grid (utility-redesign plan §8 Phase D — reuse the materialise view): one
// cell per chunk THIS tab has computed, coloured by which job it helped. Same shared canvas
// as the submitter's RunVisual, framed for the worker role. jobId (a string) → a stable small
// category index so colours are consistent within a session.
const contribCells: number[] = [];
const jobCategory = new Map<string, number>();
function categoryFor(jobId: string): number {
  let c = jobCategory.get(jobId);
  if (c === undefined) {
    c = jobCategory.size;
    jobCategory.set(jobId, c);
  }
  return c;
}
const GRID_CAP = 600; // keep the canvas bounded on a long-running contributor tab
function redrawContribGrid() {
  const canvas = el?.querySelector<HTMLCanvasElement>("#contrib-grid");
  if (canvas) drawCellGrid(canvas, contribCells);
}
function updateContribLegend() {
  const legend = el?.querySelector<HTMLElement>("#contrib-legend");
  if (!legend) return;
  legend.innerHTML = [...jobCategory.entries()]
    .map(
      ([jobId, cat]) =>
        `<span class="rv-legend-item"><span class="rv-swatch" style="background:${categoryColor(cat)}"></span>${jobId.slice(0, 6)}</span>`,
    )
    .join("");
}

export function renderContribute(): void {
  const playground = document.getElementById("playground");
  if (playground) playground.style.display = "none";
  for (const o of document.querySelectorAll<HTMLElement>("main.about")) o.style.display = "none";

  if (!el) {
    el = document.createElement("main");
    el.className = "container contribute";
    el.innerHTML = pageHtml();
    document.body.appendChild(el);
    wire();
    window.addEventListener("hashchange", () => {
      if (location.hash !== "#contribute") leave();
    });
  }
  el.style.display = "";
  window.scrollTo(0, 0);
}

function leave() {
  stop();
  if (el) el.style.display = "none";
  // Only restore the playground if we're not heading into the About overlay (its renderer
  // handles its own visibility).
  if (location.hash !== "#about") {
    const playground = document.getElementById("playground");
    if (playground) playground.style.display = "";
  }
}

function pageHtml(): string {
  const noUrl = !MESH_URL;
  return `
    <a class="back-link" href="#">← Back to the playground</a>
    <h1>Contribute compute</h1>
    <p class="lede">
      Donate your browser's spare cycles to whatever Monte Carlo jobs are running on the mesh.
      Your tab pulls small, deterministic chunks, computes them, and sends the results back —
      the same work the submitter's own machine would do, spread across everyone who's helping.
    </p>

    ${
      noUrl
        ? `<div class="card consent-card"><p class="muted">No mesh coordinator is configured for this build, so there's nothing to contribute to. Set <span class="mono">VITE_MESH_URL</span> and rebuild, or run the coordinator locally (<span class="mono">docker compose up</span> in <span class="mono">coordinator/</span>).</p></div>`
        : `
    <div class="card consent-card" id="consent-card">
      <div class="consent-head">
        <span class="consent-dot idle" id="consent-dot"></span>
        <span id="consent-state">Not contributing</span>
      </div>
      <p class="consent-copy" id="consent-copy">
        Nothing runs until you choose to start. While contributing, your tab will use some CPU
        to compute chunks; you can stop anytime, and a cap keeps it from monopolising a core.
      </p>
      <div class="consent-controls">
        <button class="btn" id="contribute-toggle">Start contributing</button>
        <label class="duty-field">
          <span>CPU cap</span>
          <select id="duty-select">
            <option value="0.25">Light (25%)</option>
            <option value="0.5">Medium (50%)</option>
            <option value="0.75" selected>High (75%)</option>
            <option value="1">Max (100%)</option>
          </select>
        </label>
      </div>
      <p class="safety-note">
        Kernels you compute are checked by the determinism linter and run sandboxed in this
        Web Worker with no network access. That blocks the common abuse paths, but it is
        best-effort — only contribute if you're comfortable running community jobs.
      </p>
    </div>

    <div class="stat-row contribute-stats">
      <div class="stat"><span class="s-label">chunks contributed</span><span class="s-value" id="stat-chunks">0</span></div>
      <div class="stat"><span class="s-label">workers online</span><span class="s-value" id="stat-workers">—</span></div>
      <div class="stat"><span class="s-label">status</span><span class="s-value" id="stat-status">idle</span></div>
    </div>

    <div class="section-head" style="margin-top:24px">Your contribution</div>
    <div class="card rv-card" id="contrib-card" hidden>
      <div class="rv-head">
        <span class="rv-title">Chunks you've computed</span>
        <span class="rv-legend" id="contrib-legend"></span>
      </div>
      <canvas class="rv-grid" id="contrib-grid" aria-label="Grid of chunks this tab has computed, coloured by job"></canvas>
      <div class="rv-sub muted" id="contrib-grid-sub">Each square is a chunk you computed; colour = which job it helped.</div>
    </div>

    <div class="section-head" style="margin-top:24px">Recent activity</div>
    <div class="feed" id="feed"><div class="feed-empty muted">Start contributing to see chunks stream in.</div></div>
    `
    }
  `;
}

function wire() {
  if (!el || !MESH_URL) return;
  const toggle = el.querySelector<HTMLButtonElement>("#contribute-toggle");
  const dutySel = el.querySelector<HTMLSelectElement>("#duty-select");
  toggle?.addEventListener("click", () => (handle ? stop() : start()));
  dutySel?.addEventListener("change", () => {
    duty = Number(dutySel.value) || 0.75;
    if (handle) {
      // Re-establish with the new cap (the cap is set at start).
      stop();
      start();
    }
  });
}

function start() {
  if (!MESH_URL || handle) return;
  setState("connecting");
  handle = startWorkerClient({
    url: MESH_URL,
    dutyCycle: duty,
    onStatus: (s) => setState(s === "connected" ? "contributing" : s === "closed" ? "reconnecting" : "connecting"),
    onStats: (s: StatsMsg) => setText("stat-workers", String(s.total)),
    onChunk: (info) => {
      chunksDone++;
      setText("stat-chunks", String(chunksDone));
      // Materialise grid: reveal it on the first chunk, then append a cell coloured by which
      // job this chunk helped.
      const card = el?.querySelector<HTMLElement>("#contrib-card");
      if (card) card.hidden = false;
      contribCells.push(categoryFor(info.jobId));
      if (contribCells.length > GRID_CAP) contribCells.shift();
      redrawContribGrid();
      updateContribLegend();
      pushFeed(`computed chunk #${info.chunkIndex} · job ${info.jobId.slice(0, 8)}`);
    },
  });
  setToggle("Stop contributing", true);
}

function stop() {
  handle?.stop();
  handle = null;
  setState("stopped");
  setToggle("Start contributing", false);
}

// ── small DOM helpers ──
function setText(id: string, v: string) {
  const node = el?.querySelector<HTMLElement>("#" + id);
  if (node) node.textContent = v;
}

function setToggle(label: string, active: boolean) {
  const btn = el?.querySelector<HTMLButtonElement>("#contribute-toggle");
  if (btn) {
    btn.textContent = label;
    btn.classList.toggle("btn-stop", active);
  }
}

function setState(state: "connecting" | "contributing" | "reconnecting" | "stopped") {
  const dot = el?.querySelector<HTMLElement>("#consent-dot");
  const label = el?.querySelector<HTMLElement>("#consent-state");
  const map: Record<string, [string, string]> = {
    connecting: ["connecting", "busy"],
    contributing: ["Contributing — thank you", "live"],
    reconnecting: ["reconnecting…", "busy"],
    stopped: ["Not contributing", "idle"],
  };
  const [text, cls] = map[state]!;
  if (label) label.textContent = text;
  if (dot) dot.className = "consent-dot " + cls;
  setText("stat-status", state === "contributing" ? "live" : state);
}

const FEED_MAX = 12;
function pushFeed(line: string) {
  const feed = el?.querySelector<HTMLElement>("#feed");
  if (!feed) return;
  const empty = feed.querySelector(".feed-empty");
  if (empty) empty.remove();
  const row = document.createElement("div");
  row.className = "feed-row";
  row.innerHTML = `<span class="feed-time">${new Date().toLocaleTimeString()}</span> ${line}`;
  feed.prepend(row);
  while (feed.children.length > FEED_MAX) feed.lastElementChild?.remove();
}

// Stop contributing if the tab is closed/navigated away (belt-and-braces; the socket close
// also triggers a coordinator-side requeue).
window.addEventListener("beforeunload", () => handle?.stop());
